import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Download, Upload } from 'lucide-react';
import DataTable from '../common/DataTable';
import CategoryForm from './CategoryForm';
import ConfirmationModal from '../common/ConfirmationModal';
import { inventoryService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useConfirmation } from '../../hooks/useConfirmation';

interface Category {
  id: number;
  name: string;
  description: string;
  parent_id?: number;
  parent_name?: string;
  is_active: boolean;
}

const CategoryManagement: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>(undefined);
  const [resetForm, setResetForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { showToast } = useToast();
  const { confirmationState, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();

  useEffect(() => {
    loadCategories(1);
  }, []);

  const loadCategories = async (page: number, search?: string) => {
    try {
      setLoading(true);
      const searchText = search !== undefined ? search : searchTerm;
      const response = await inventoryService.getCategories({ 
        page, 
        per_page: 10, 
        search: searchText 
      });
      setCategories(response.data);
      setTotalItems(response.total || 0);
      setCurrentPage(response.page || page);
      if (search !== undefined) {
        setSearchTerm(search);
      }
    } catch (error) {
      showToast('error', 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (search: string) => {
    setSearchTerm(search);
    if (search) {
      await loadCategories(1, search);
    } else {
      await loadCategories(1, '');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
  };

  const handleSave = async (categoryData: any) => {
    try {
      if (editingCategory) {
        await inventoryService.updateCategory(editingCategory.id, categoryData);
        showToast('success', 'Category updated successfully');
      } else {
        await inventoryService.createCategory(categoryData);
        showToast('success', 'Category created successfully');
      }
      setEditingCategory(undefined);
      setResetForm(true);
      setTimeout(() => setResetForm(false), 100);
      loadCategories(currentPage);
    } catch (error) {
      showToast('error', 'Failed to save category');
    }
  };

  const handleCancel = () => {
    setEditingCategory(undefined);
    setResetForm(true);
    setTimeout(() => setResetForm(false), 100);
  };

  const downloadTemplate = async () => {
    try {
      const blob = await inventoryService.downloadCategoriesTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'categories_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('success', 'Template downloaded successfully');
    } catch (error) {
      showToast('error', 'Failed to download template');
    }
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      await inventoryService.importCategories(file);
      showToast('success', 'Categories imported successfully');
      loadCategories(1);
    } catch (error) {
      showToast('error', 'Failed to import categories');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleToggleCollapse = () => {
    setIsFormCollapsed(!isFormCollapsed);
  };

  const handleDelete = async (category: Category) => {
    showConfirmation(
      {
        title: 'Delete Category',
        message: `Are you sure you want to delete category "${category.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        variant: 'danger'
      },
      async () => {
        try {
          await inventoryService.deleteCategory(category.id);
          showToast('success', 'Category deleted successfully');
          loadCategories(currentPage);
        } catch (error) {
          showToast('error', 'Failed to delete category');
        }
      }
    );
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Category Name' },
    { 
      key: 'parent_name', 
      label: 'Parent Category',
      render: (value: string) => value || '-'
    },
    { key: 'description', label: 'Description' },
    {
      key: 'is_active',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <div style={{ padding: 'var(--erp-spacing-lg)' }}>
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 flex justify-between items-center" style={{ padding: 'var(--erp-section-padding)' }}>
          <h2 className="font-medium text-gray-800" style={{ fontSize: 'var(--erp-datatable-title-font-size)', lineHeight: 'var(--erp-line-height)' }}>
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h2>
          <div className="flex items-center" style={{ gap: 'var(--erp-spacing-sm)' }}>
            <button
              type="button"
              onClick={downloadTemplate}
              className="erp-form-btn erp-btn-template"
            >
              <Download className="erp-form-btn-icon" />
              Template
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="erp-form-btn erp-btn-import"
            >
              <Upload className="erp-form-btn-icon" />
              Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileImport}
              className="hidden"
            />
            <button
              type="button"
              onClick={handleToggleCollapse}
              className="text-gray-500 hover:text-gray-700"
            >
              {isFormCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>
        </div>

        {!isFormCollapsed && (
          <CategoryForm
            category={editingCategory}
            onSave={handleSave}
            onCancel={handleCancel}
            isCollapsed={false}
            onToggleCollapse={() => {}}
            resetForm={resetForm}
          />
        )}
      </div>
      
      <DataTable
        title="Category Management"
        columns={columns}
        data={categories}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading}
        pageSize={10}
        totalItems={totalItems}
        currentPage={currentPage}
        onPageChange={(page) => loadCategories(page)}
        onSearch={handleSearch}
      />
      
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        variant={confirmationState.variant}
        onConfirm={handleConfirm}
        onCancel={hideConfirmation}
      />
    </div>
  );
};

export default CategoryManagement;